import { type FC, useEffect, useMemo, useState } from "react";
import { FreeWordHighlighter } from "../../routes/emails/SearchEmailsPage/components/FreeWordHighlighter";
import { highlightHtmlContent, isHtmlContent } from "../../utils/html-utils";
import { HtmlEmailPreview } from "../HtmlEmailPreview";
import { ToggleUI } from "../Toggle/Toggle";

type EmailContentViewProps = {
  content: string;
  freeWords: string[];
  highlighterType: "Modal" | "Table";
  className?: string;
};

/**
 * メール本文を表示するコンポーネント（HTML検出時はプレビュー切り替え可能）
 * @param {EmailContentViewProps} props
 * @return {JSX.Element} メール本文表示
 */
export const EmailContentView: FC<EmailContentViewProps> = ({
  content,
  freeWords,
  highlighterType,
  className = "",
}) => {
  const hasHtml = useMemo(() => isHtmlContent(content), [content]);
  const highlightedHtml = useMemo(
    () => (hasHtml ? highlightHtmlContent(content, freeWords) : content),
    [hasHtml, content, freeWords],
  );
  const [showHtmlPreview, setShowHtmlPreview] = useState(hasHtml);

  useEffect(() => {
    setShowHtmlPreview(hasHtml);
  }, [hasHtml]);

  return (
    <div className={className}>
      {hasHtml && (
        <div className="flex items-center justify-end mb-2">
          <ToggleUI
            label="HTMLプレビュー"
            toggleSize="toggle-sm"
            checked={showHtmlPreview}
            onChange={e => setShowHtmlPreview(e.target.checked)}
          />
        </div>
      )}

      {showHtmlPreview && hasHtml ? (
        <HtmlEmailPreview htmlContent={highlightedHtml} />
      ) : (
        <div className="whitespace-pre-line text-sm">
          <FreeWordHighlighter
            type={highlighterType}
            freeWords={freeWords}
            textToHighlight={content}
          />
        </div>
      )}
    </div>
  );
};
